import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import { LoginPage } from './pages/LoginPage';
import { OAuthCallback } from './pages/OAuthCallback';
import { ScansPage } from './pages/ScansPage';
import { ScanDetailPage } from './pages/ScanDetailPage';
import { FailuresPage } from './pages/FailuresPage';
import { KbBrowserPage } from './pages/KbBrowserPage';
import { KbDetailPage } from './pages/KbDetailPage';
import { AggregationsPage } from './pages/AggregationsPage';
import { AppUsersPage } from './pages/AppUsersPage';
import { AppUserDetailPage } from './pages/AppUserDetailPage';
import { NewAppUserPage } from './pages/NewAppUserPage';
import { ScanAnalysisQueuePage } from './pages/ScanAnalysisQueuePage';
import { isAuthenticated } from './lib/auth';
import { buildReturnToFromLocation, storeReturnTo } from './lib/returnTo';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  if (!isAuthenticated()) {
    // Capture where the user was trying to go so OAuthCallback can bring
    // them back after Cognito sign-in. Without this, every deep link
    // (e.g. Slack alerts → /scan-analysis-queue) gets clobbered to the
    // default /scans landing.
    storeReturnTo(buildReturnToFromLocation(window.location));
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/oauth/callback" element={<OAuthCallback />} />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <AppShell />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/scans" replace />} />
          <Route path="scans" element={<ScansPage />} />
          <Route path="scans/:scanId" element={<ScanDetailPage />} />
          <Route path="failures" element={<FailuresPage />} />
          <Route path="kb" element={<KbBrowserPage />} />
          <Route path="kb/:vehicleKey" element={<KbDetailPage />} />
          <Route path="aggregations" element={<AggregationsPage />} />
          <Route path="app-users" element={<AppUsersPage />} />
          <Route path="app-users/new" element={<NewAppUserPage />} />
          <Route path="app-users/:userId" element={<AppUserDetailPage />} />
          {/* Pending-approval page was merged into /app-users as a top
              section. Slack alerts and bookmarks may still link here —
              redirect rather than 404. */}
          <Route path="app-users/pending" element={<Navigate to="/app-users" replace />} />
          <Route path="scan-analysis-queue" element={<ScanAnalysisQueuePage />} />
          {/* Settings page was merged into /scan-analysis-queue as a
              collapsible panel. Same redirect rationale as above. */}
          <Route
            path="scan-analysis-settings"
            element={<Navigate to="/scan-analysis-queue" replace />}
          />
          {/* /app-change-digest was removed per PR #120 — its aggregations
              were a duplicate of the per-scan analysis review tab. Redirect
              old bookmarks rather than 404. */}
          <Route
            path="app-change-digest"
            element={<Navigate to="/scan-analysis-queue" replace />}
          />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
